<!--
  ReportSectionXyGraph.vue  —  Engine E section renderer for server type "xy_graph".
  Purpose: render an echarts line/area chart from an xy_graph report section.
    Ports the recovered component (RECOVERY/analysis/history-reports/ReportSectionXyGraph.vue,
    chunk-6e29658c/3ef8.js). echarts is loaded as a lazy, guarded async import so
    the rest of the app builds/renders even if echarts is absent.

  reconstructed from lacak.io deployed behavior; app-specific contract verbatim,
  internal impl re-authored.

  VERBATIM (3ef8.js:81-137):
    - component name ...... "report-section-xy-graph"
    - AXIS_TYPE ........... { number_axis: "value" }
    - PAINT registry ...... line_paint, area_paint (both echarts type:"line";
                            area adds a LinearGradient(0,0,0,1,[base_color -> #fff]))
    - section fields ...... x_axis{type,label}, range_axes[{type,label,range_start,range_end}],
                            annotations[{x,y,text,color}],
                            data_sets[{range_axis_index,paint{type,base_color},points[{x,y}]}]
    - computed names ...... xAxis, yAxis, markers, series, options
    - echarts options ..... tooltip{trigger:"axis",axisPointer:{type:"cross"}},
                            dataZoom:[{type:"inside"},{type:"slider"}], symbol:"none",
                            xAxis.max:"dataMax", y axes alternate left/right ((i+1)%2),
                            splitLine.show only after first axis,
                            boundaryGap ["0%","20%"] when annotations present,
                            annotations -> markPoint on the FIRST series only
    - events (chart.on) ... showTip/hideTip -> "tooltip"; click -> "marker-click"
                            (componentType==="markPoint") else "click"
    - invalidateLayout .... chart.resize()
-->
<template>
  <v-card class="report-section-xy-graph">
    <v-card-title v-if="section.header" class="report-section-xy-graph__title">
      {{ section.header }}
    </v-card-title>
    <div ref="chart" class="report-section-xy-graph__chart" />
  </v-card>
</template>

<script>
// x-axis type -> echarts axis type   [RECOVERED VERBATIM: 3ef8.js L81-83]
const AXIS_TYPE = {
  number_axis: 'value',
};

// paint registry: section.data_sets[i].paint.type -> echarts series descriptor.
// Two paint kinds ship: LINE and AREA.  [RECOVERED VERBATIM: 3ef8.js L84-110]
// (area's gradient is guarded so the series still builds if echarts is absent.)
const PAINT = {
  line_paint(paint) {
    return {
      type: 'line',
      itemStyle: { color: paint.base_color },
    };
  },
  area_paint(paint, echarts) {
    const descriptor = {
      type: 'line',
      itemStyle: { color: paint.base_color },
    };
    if (echarts && echarts.graphic && echarts.graphic.LinearGradient) {
      descriptor.areaStyle = {
        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
          { offset: 0, color: paint.base_color },
          { offset: 1, color: '#fff' },
        ]),
      };
    }
    return descriptor;
  },
};

export default {
  name: 'report-section-xy-graph', // [RECOVERED VERBATIM]

  props: {
    section: { type: Object, required: true }, // server report "xy_graph" section
  },

  computed: {
    // single x axis: numeric "value" axis, autoscale to dataMax  [VERBATIM shape]
    xAxis() {
      const x = this.section.x_axis || {};
      return {
        type: AXIS_TYPE[x.type],
        max: 'dataMax',
        name: x.label,
      };
    },

    // one or more (range) y axes; alternate left/right; obey per-axis min/max.
    // When annotations exist, add a 20% top boundary gap so markers have room.
    yAxis() {
      const axes = this.section.range_axes || [];
      const extra = {};
      if (this.section.annotations && this.section.annotations.length) {
        extra.boundaryGap = ['0%', '20%'];
      }
      return axes.map((axis, i) => ({
        splitLine: { show: !!i },
        type: AXIS_TYPE[axis.type],
        name: axis.label,
        position: (i + 1) % 2 ? 'left' : 'right',
        min: axis.range_start,
        max: axis.range_end,
        ...extra,
      }));
    },

    // annotations -> echarts markPoint entries at [x,y]  [VERBATIM shape]
    markers() {
      if (!this.section.annotations || !this.section.annotations.length) {
        return null;
      }
      return this.section.annotations.map(({ x, y, text, color }) => ({
        name: 'coordinate',
        coord: [x, y],
        value: text,
        itemStyle: { color },
      }));
    },

    // each data_set -> an echarts series; markers attach to the FIRST series only
    series() {
      const dataSets = this.section.data_sets || [];
      return dataSets.map((ds, i) => {
        const paint = PAINT[ds.paint.type](ds.paint, this.echarts);
        const mark =
          !i && this.markers ? { markPoint: { data: this.markers } } : {};
        return {
          data: ds.points.map((p) => [p.x, p.y]),
          grid: false,
          yAxisIndex: ds.range_axis_index,
          symbol: 'none',
          ...paint,
          ...mark,
        };
      });
    },

    // full echarts option: cross tooltip + inside/slider zoom  [VERBATIM]
    options() {
      return {
        tooltip: { trigger: 'axis', axisPointer: { type: 'cross' } },
        dataZoom: [{ type: 'inside' }, { type: 'slider' }],
        xAxis: this.xAxis,
        yAxis: this.yAxis,
        series: this.series,
      };
    },
  },

  // echarts is a lazily loaded async chunk  [VERBATIM: n.e("echarts")...313e].
  // Guarded: a failed/absent echarts resolves to null instead of throwing so the
  // rest of the report still renders.
  beforeCreate() {
    this.echarts = null;
    this.chart = null;
    this.echartsPromise = import('echarts')
      .then((mod) => [mod && mod.default ? mod.default : mod])
      .catch(() => [null]);
  },

  mounted() {
    this.echartsPromise.then(([echarts]) => {
      if (!echarts) return; // echarts not installed / failed to load
      this.echarts = echarts;
      this.update();
      this.observeResize();
    });
  },

  beforeDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    if (this.onWindowResize && typeof window !== 'undefined') {
      window.removeEventListener('resize', this.onWindowResize);
      this.onWindowResize = null;
    }
    if (this.chart) {
      this.chart.dispose();
      this.chart = null;
    }
  },

  watch: {
    // Re-render when the section document changes (only meaningful once echarts
    // has loaded; update() no-ops otherwise).
    options() {
      this.update();
    },
  },

  methods: {
    update() {
      if (!this.echarts || !this.$refs.chart) return;
      if (!this.chart) {
        this.chart = this.echarts.init(this.$refs.chart);
        this.bindEvents();
      }
      if (this.options && this.chart) {
        this.chart.setOption({ ...this.options });
      }
    },

    bindEvents() {
      let last;
      this.chart.on('showTip', (e) => {
        last = e;
        this.$emit('tooltip', { show: true, data: e });
      });
      this.chart.on('hideTip', () => {
        this.$emit('tooltip', { show: false, data: last });
      });
      this.chart.on('click', (e) => {
        e.componentType === 'markPoint'
          ? this.$emit('marker-click', e)
          : this.$emit('click', e);
      });
    },

    // Recovered <resize-sensor> -> invalidateLayout replaced with a native
    // ResizeObserver (falls back to window resize) so there is no external dep.
    observeResize() {
      if (typeof ResizeObserver !== 'undefined' && this.$refs.chart) {
        this.resizeObserver = new ResizeObserver(() => this.invalidateLayout());
        this.resizeObserver.observe(this.$refs.chart);
      } else if (typeof window !== 'undefined') {
        this.onWindowResize = () => this.invalidateLayout();
        window.addEventListener('resize', this.onWindowResize);
      }
    },

    invalidateLayout() {
      if (this.chart) this.chart.resize();
    },
  },
};
</script>

<style scoped>
.report-section-xy-graph__chart {
  width: 100%;
  min-height: 320px;
}
</style>
